import { useMemo, useState } from 'react';
import ReactHotkeys from 'react-hot-keys';
import styled from 'styled-components';

import ClickSvg from '../../assets/icons/ClickSvg';
import { useAttachmentContent } from '../../hooks/useAttachmentContent';
import { LessonAttachment } from '../../services/backendService/backendService';
import { IconWrapper } from '../_atoms/IconWrapper';
import { ReadAloudModal } from '../ReadAloudModal';
import ReadAloudTextViewer from './ReadAloudTextViewer';
import { parseSlateStringToText } from './slateUtil';
import { SlideElementAttachmentPassageType } from './slide.types';

const readingStateDefaults = { size: 24 };

export const ReadAloud = ({
  lessonAttachment,
  isFromDiscover,
}: {
  lessonAttachment: LessonAttachment;
  isFromDiscover: boolean;
}) => {
  const { content } = useAttachmentContent(lessonAttachment, isFromDiscover);

  const [readingState, setReadingState] = useState<{ size: number }>(
    readingStateDefaults,
  );

  const handleIncreaseFontSize = () => {
    setReadingState(prev => ({ ...prev, size: prev.size + 2 }));
  };
  const handleReduceFontSize = () => {
    setReadingState(prev => ({ ...prev, size: prev.size - 2 }));
  };

  const contentString = useMemo(() => {
    if (!content) return '';
    return JSON.stringify(content);
  }, [content]);
  return (
    <ReadAloudContainer>
      <ControlPanelContainer>
        <ControlPanel>
          <ControlPanelButtonEnlarge onClick={handleIncreaseFontSize}>
            <span>A</span>
          </ControlPanelButtonEnlarge>
          <ControlPanelButtonMinimize onClick={handleReduceFontSize}>
            <span>A</span>
          </ControlPanelButtonMinimize>
        </ControlPanel>
      </ControlPanelContainer>
      <ReadAloudTextViewer
        content={contentString}
        id={lessonAttachment.attachmentId}
        forceState={{
          size: readingState.size,
          align: 'left',
          color: 'black',
        }}
      />
    </ReadAloudContainer>
  );
};

/** Same as ReadAloud but takes content directly (Slate JSON string) instead of loading via useAttachmentContent. */
export const ReadAloudWithContent = ({
  content,
  id = 'read-aloud-with-content',
}: {
  content: string;
  id?: string;
}) => {
  const [readingState, setReadingState] = useState<{ size: number }>(
    readingStateDefaults,
  );

  const handleIncreaseFontSize = () => {
    setReadingState(prev => ({ ...prev, size: prev.size + 2 }));
  };
  const handleReduceFontSize = () => {
    setReadingState(prev => ({ ...prev, size: prev.size - 2 }));
  };

  return (
    <ReadAloudContainer>
      <ControlPanelContainer>
        <ControlPanel>
          <ControlPanelButtonEnlarge onClick={handleIncreaseFontSize}>
            <span>A</span>
          </ControlPanelButtonEnlarge>
          <ControlPanelButtonMinimize onClick={handleReduceFontSize}>
            <span>A</span>
          </ControlPanelButtonMinimize>
        </ControlPanel>
      </ControlPanelContainer>
      <ReadAloudTextViewer
        content={content}
        id={id}
        forceState={{
          size: readingState.size,
          align: 'left',
          color: 'black',
        }}
      />
    </ReadAloudContainer>
  );
};

const ControlPanelButton = styled.div`
  color: white;
  width: 100%;
  text-align: center;
  height: 50px;
  transition: all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);
  display: flex;
  justify-content: center;
  align-items: center;

  &:hover {
    background: black;
  }
  border-radius: 5px;
  padding: 10px;
  span {
    user-select: none;
  }
`;

const ControlPanelButtonMinimize = styled(ControlPanelButton)`
  font-size: 24px;
  line-height: 20px;
`;
const ControlPanelButtonEnlarge = styled(ControlPanelButton)`
  font-size: 38px;
  line-height: 32px;
`;

const ControlPanelContainer = styled.div`
  position: absolute;
  top: 50%;
  left: -85px;
  transform: translateY(-50%);
  border-radius: 10px;
  padding: 5px;
  background: rgba(17, 23, 29, 0.6);
  color: white;
  box-shadow: 0px 0px 0px 1px rgba(64, 87, 109, 0.04),
    0px 6px 20px -4px rgba(64, 87, 109, 0.3);

  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);
`;

const ControlPanel = styled.div`
  position: relative;
`;

const ReadAloudContainer = styled.div`
  background: white;
  height: 100%;
  width: 100%;
  overflow-y: auto;
`;

const SlideElementAttachmentPassage = ({
  element,
  lessonAttachments,
  slideMode,
  isFromDiscover,
}: {
  element: SlideElementAttachmentPassageType;
  style?: React.CSSProperties;
  lessonAttachments?: LessonAttachment[];
  slideMode: 'view' | 'edit';
  isFromDiscover: boolean;
}) => {
  const lessonAttachment = useMemo(
    () => lessonAttachments?.find(l => l.attachmentId === element.attachmentId),
    [lessonAttachments, element.attachmentId],
  );

  const title = useMemo(() => {
    if (!lessonAttachment) return 'No title';
    return parseSlateStringToText(lessonAttachment.title);
  }, [lessonAttachment]);

  const [showReadAloud, setShowReadAloud] = useState(false);

  return (
    <div
      style={{
        background: '#3e7e69',
        height: '100%',
        width: '100%',
        borderRadius: 10,
        cursor: slideMode === 'edit' ? 'default' : 'pointer',
        boxShadow: '4px 8px 1px #61b195',
        position: 'relative',
        overflow: 'hidden',
        padding: '20px',
      }}
      onClick={() => {
        if (slideMode === 'view') {
          setShowReadAloud(true);
        }
      }}
    >
      <ReactHotkeys
        keyName={'Escape'}
        onKeyDown={(_, event) => {
          event.preventDefault();
          if (showReadAloud) {
            setShowReadAloud(false);
          }
        }}
      />
      <TopCircle />
      <ClickIconContainer>
        <IconWrapper color="#61b195" height="40%" width="40%">
          <ClickSvg />
        </IconWrapper>
      </ClickIconContainer>

      {showReadAloud && lessonAttachment && (
        <ReadAloudModal onClose={() => setShowReadAloud(false)}>
          <ReadAloud
            lessonAttachment={lessonAttachment}
            isFromDiscover={isFromDiscover}
          />
        </ReadAloudModal>
      )}
      <span
        style={{
          position: 'absolute',
          top: '45%',
          left: 20,
          width: 'calc(100% - 40px)',
          textAlign: 'center',
          color: 'white',
          fontSize: `${element.width / 12}px`,
          userSelect: 'none',
        }}
      >
        {title}
      </span>
    </div>
  );
};

const ClickIconContainer = styled.div`
  position: absolute;
  top: 15%;
  left: 45%;
  height: 40%;
  width: 40%;
  display: grid;
  justify-content: left;
  align-items: start;
`;

const TopCircle = styled.div`
  height: 50%;
  width: 50%;

  position: absolute;
  top: 0;
  left: 50%;
  transform: translate(-50%, -40%);

  border-radius: 9999px;
  background: #93c8b6;
  box-shadow: 0px 8px 1px #61b195;
`;

export default SlideElementAttachmentPassage;
